import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://compliance.kurukshetraanalytics.com/backend/api';

const pub = axios.create({ baseURL: BASE_URL, timeout: 120000 });

// ── Document metadata ──────────────────────────────────────────────────────────
const DOC_META = {
  driving_license:    { label: 'Driving License',               needsExpiry: true,  needsNumber: true,  hint: 'Front and back of your driver\'s license.' },
  passport:           { label: 'Passport',                      needsExpiry: true,  needsNumber: true,  hint: 'Clear photo/scan of the bio-data page.' },
  visa_copy:          { label: 'US Visa',                       needsExpiry: true,  needsNumber: false, hint: 'Visa stamp page inside your passport.' },
  i94:                { label: 'I-94 Arrival Record',           needsExpiry: false, needsNumber: false, hint: 'Download latest I-94 from cbp.dhs.gov/i94.' },
  ssn_card:           { label: 'Social Security Card',          needsExpiry: false, needsNumber: false, needsSSN4: true,  hint: 'Upload your SSN card. Enter last 4 digits of your SSN.' },
  ead_card:           { label: 'EAD Card (Work Authorization)', needsExpiry: true,  needsNumber: true,  needsUSCIS: true, hint: 'Both front and back if possible.' },
  i20_certificate:    { label: 'I-20 Certificate',              needsExpiry: false, needsNumber: true,  hint: 'Most recent I-20 with all pages.' },
  i983_form:          { label: 'I-983 Training Plan',           needsExpiry: false, needsNumber: false, hint: 'STEM OPT training plan form.' },
  i797:               { label: 'I-797 Approval Notice',         needsExpiry: true,  needsNumber: false, hint: 'H-1B petition approval notice.' },
  gc_card:            { label: 'Green Card (I-551)',             needsExpiry: true,  needsNumber: true,  hint: 'Permanent Resident Card.' },
  degree_bachelors:    { label: "Bachelor's Degree Certificate",  needsExpiry: false, needsNumber: false, isDegree: true, hint: "Upload your Bachelor's degree certificate or official transcript." },
  degree_masters:      { label: "Master's Degree Certificate",   needsExpiry: false, needsNumber: false, isDegree: true, hint: "Upload your Master's degree certificate or official transcript." },
  resume:              { label: 'Resume / CV',                   needsExpiry: false, needsNumber: false, isOptional: true, hint: 'Your latest resume in PDF format (optional).' },
  signed_offer_letter: { label: 'Signed Offer Letter',          needsExpiry: false, needsNumber: false, hint: 'Please sign the offer letter and upload the signed copy here.' },
};

// ── Required docs by visa type ─────────────────────────────────────────────────
const COMMON  = ['driving_license', 'passport', 'ssn_card', 'visa_copy', 'i94'];
const DEGREES = ['degree_bachelors', 'degree_masters'];
const VISA_DOCS = {
  OPT:         [...COMMON, 'ead_card', 'i20_certificate', ...DEGREES, 'resume'],
  STEM_OPT:    [...COMMON, 'ead_card', 'i20_certificate', 'i983_form', ...DEGREES, 'resume'],
  CPT:         [...COMMON, 'i20_certificate', ...DEGREES, 'resume'],
  H1B:         [...COMMON, 'i797', ...DEGREES, 'resume'],
  H4_EAD:      [...COMMON, 'ead_card', ...DEGREES, 'resume'],
  GC:          ['driving_license', 'passport', 'ssn_card', 'gc_card', ...DEGREES, 'resume'],
  CITIZENSHIP: ['driving_license', 'passport', 'ssn_card', ...DEGREES, 'resume'],
  Other:       [...COMMON, ...DEGREES, 'resume'],
};

function getMeta(type) {
  return DOC_META[type] || { label: type.replace(/_/g, ' '), needsExpiry: false, needsNumber: false, hint: '' };
}

// ── Preview modal ──────────────────────────────────────────────────────────────
function PreviewModal({ previewUrl, mimeType, label, onClose }) {
  const isImage = mimeType?.startsWith('image/');
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex flex-col">
      <div className="bg-white flex items-center justify-between px-4 py-3 shrink-0">
        <span className="font-semibold text-slate-800 text-sm">{label}</span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-800 text-lg font-bold px-2">✕</button>
      </div>
      <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-4">
        {isImage
          ? <img src={previewUrl} alt={label} className="max-w-full max-h-full rounded shadow-lg object-contain" />
          : <iframe src={previewUrl} title={label} className="w-full h-full rounded shadow-lg bg-white" style={{ minHeight: '70vh' }} />
        }
      </div>
    </div>
  );
}

// ── Degree card (one per degree level: degree_bachelors / degree_masters) ────────
function DegreeCard({ docType, staged, onStage, onRemove, uploadStatus }) {
  const meta    = getMeta(docType);
  const fileRef = useRef();
  const [notCompleted, setNotCompleted] = useState(staged?.notApplicable || false);
  const [preview, setPreview]           = useState(false);
  const [error,   setError]             = useState('');

  const isDone      = uploadStatus === 'done';
  const isUploading = uploadStatus === 'uploading';
  const isStaged    = !!staged;
  const hasFile     = !!staged?.file;

  function toggleNotCompleted(checked) {
    setNotCompleted(checked);
    if (checked) {
      onStage(docType, { file: null, previewUrl: null, notApplicable: true, degreeType: 'not_completed' });
    } else {
      onRemove(docType);
    }
  }

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    const allowed = ['application/pdf','image/jpeg','image/png','image/jpg'];
    if (!allowed.includes(file.type)) { setError('Allowed formats: PDF, JPG, PNG.'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('File too large. Max 20 MB.'); return; }
    const previewUrl = URL.createObjectURL(file);
    onStage(docType, { file, previewUrl, notApplicable: false, degreeType: docType === 'degree_bachelors' ? 'bachelors' : 'masters' });
  }

  if (isDone) return (
    <div className="rounded-xl border border-green-200 bg-green-50 p-5 flex items-center gap-4">
      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <p className="font-semibold text-green-800 text-sm">{meta.label}</p>
        <p className="text-green-600 text-xs mt-0.5">Submitted successfully</p>
      </div>
    </div>
  );

  if (isUploading) return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 flex items-center gap-4">
      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
      <div><p className="font-semibold text-blue-800 text-sm">{meta.label}</p><p className="text-blue-600 text-xs">Uploading…</p></div>
    </div>
  );

  const borderCls = notCompleted
    ? 'border-amber-200 bg-amber-50'
    : hasFile
    ? 'border-blue-200 bg-blue-50'
    : 'border-slate-200 bg-white';

  return (
    <div className={`rounded-xl border overflow-hidden ${borderCls}`}>
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
            notCompleted ? 'bg-amber-400 text-white' : hasFile ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
          }`}>
            {notCompleted ? '–' : hasFile ? '✓' : '↑'}
          </div>
          <div>
            <p className="font-semibold text-sm text-slate-800">
              {meta.label} <span className="text-red-500">*</span>
            </p>
            <p className="text-xs text-slate-500 mt-0.5">{meta.hint}</p>
          </div>
        </div>
        {/* Preview/Replace/Remove when file is staged */}
        {hasFile && !notCompleted && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => setPreview(true)} className="text-xs font-medium text-blue-600 hover:text-blue-800 bg-white border border-blue-200 px-2 py-1 rounded">👁 Preview</button>
            <button onClick={() => fileRef.current?.click()} className="text-xs font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 px-2 py-1 rounded">↺ Replace</button>
            <button onClick={() => { onRemove(docType); setNotCompleted(false); }} className="text-xs font-medium text-red-600 hover:text-red-800 bg-white border border-red-200 px-2 py-1 rounded">✕ Remove</button>
          </div>
        )}
      </div>

      <div className="px-5 pb-5 pt-1 space-y-3 border-t border-slate-100">
        {/* Not completed toggle */}
        <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
          <input type="checkbox" checked={notCompleted}
            onChange={e => toggleNotCompleted(e.target.checked)}
            className="rounded accent-amber-500 w-4 h-4" />
          <span className="text-xs font-medium text-slate-600">Not completed / Still studying</span>
        </label>

        {notCompleted ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
            Marked as not completed — HR has been notified. No upload needed.
          </div>
        ) : (
          <>
            {error && <p className="text-red-600 text-xs">{error}</p>}
            {!hasFile ? (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Upload Certificate / Transcript <span className="text-red-500">*</span>
                </label>
                <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile}
                  className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer" />
                <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG — max 20 MB</p>
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-white rounded-lg border border-blue-200 px-3 py-2.5">
                <span className="text-xl shrink-0">📄</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-blue-800 truncate">{staged.file.name}</p>
                  <p className="text-xs text-blue-500">{(staged.file.size / 1024).toFixed(0)} KB</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {hasFile && <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} className="hidden" />}

      {preview && staged?.previewUrl && (
        <PreviewModal previewUrl={staged.previewUrl} mimeType={staged.file?.type} label={meta.label} onClose={() => setPreview(false)} />
      )}
    </div>
  );
}

// ── Single document card ───────────────────────────────────────────────────────
function DocCard({ docType, staged, onStage, onRemove, uploadStatus, reuploadReason }) {
  const meta    = getMeta(docType);
  const fileRef = useRef();
  const [docNumber,  setDocNumber]  = useState(staged?.docNumber  || '');
  const [uscisNumber,setUscisNumber]= useState(staged?.uscisNumber|| '');
  const [ssn4,       setSsn4]       = useState(staged?.ssn4       || '');
  const [expiry,     setExpiry]     = useState(staged?.expiry     || '');
  const [naExpiry,   setNaExpiry]   = useState(staged?.naExpiry   || false);
  const [preview,    setPreview]    = useState(false);
  const [error,      setError]      = useState('');

  const isStaged    = !!staged;
  const isImage     = staged?.file?.type?.startsWith('image/');
  const isDone      = uploadStatus === 'done';
  const isUploading = uploadStatus === 'uploading';
  const hasFailed   = uploadStatus === 'error';
  const isOptional  = meta.isOptional;

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    const allowed = ['application/pdf','image/jpeg','image/png','image/jpg'];
    if (!allowed.includes(file.type)) { setError('Allowed formats: PDF, JPG, PNG.'); return; }
    if (file.size > 20 * 1024 * 1024) { setError('File too large. Max 20 MB.'); return; }
    const previewUrl = URL.createObjectURL(file);
    onStage(docType, { file, previewUrl, docNumber, uscisNumber, ssn4, expiry, naExpiry });
  }

  function syncDetails(overrides = {}) {
    if (staged) onStage(docType, { ...staged, docNumber, uscisNumber, ssn4, expiry, naExpiry, ...overrides });
  }

  const inputCls = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  if (isDone) return (
    <div className="rounded-xl border border-green-200 bg-green-50 p-5 flex items-center gap-4">
      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div>
        <p className="font-semibold text-green-800 text-sm">{meta.label}</p>
        <p className="text-green-600 text-xs mt-0.5">Uploaded successfully</p>
      </div>
    </div>
  );

  if (isUploading) return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-5 flex items-center gap-4">
      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
        <svg className="w-5 h-5 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
        </svg>
      </div>
      <div><p className="font-semibold text-blue-800 text-sm">{meta.label}</p><p className="text-blue-600 text-xs">Uploading…</p></div>
    </div>
  );

  return (
    <div className={`rounded-xl border overflow-hidden ${
      hasFailed ? 'border-red-200 bg-red-50' :
      isStaged  ? 'border-blue-200 bg-blue-50' :
                  'border-slate-200 bg-white'
    }`}>
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold ${
            isStaged ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
          }`}>{isStaged ? '✓' : '↑'}</div>
          <div>
            <p className={`font-semibold text-sm ${isStaged ? 'text-blue-800' : 'text-slate-800'}`}>
              {meta.label} {!isOptional && <span className="text-red-500">*</span>}
              {isOptional && <span className="text-slate-400 font-normal text-xs ml-1">(optional)</span>}
            </p>
            {meta.hint && <p className="text-xs text-slate-500 mt-0.5">{meta.hint}</p>}
          </div>
        </div>
        {isStaged && (
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview(true)} className="text-xs font-medium text-blue-600 hover:text-blue-800 bg-white border border-blue-200 px-2 py-1 rounded">👁 Preview</button>
            <button onClick={() => fileRef.current?.click()} className="text-xs font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 px-2 py-1 rounded">↺ Replace</button>
            <button onClick={() => onRemove(docType)} className="text-xs font-medium text-red-600 hover:text-red-800 bg-white border border-red-200 px-2 py-1 rounded">✕ Remove</button>
          </div>
        )}
      </div>

      {/* Staged preview strip */}
      {isStaged && staged?.file && (
        <div className="px-5 pb-3 flex items-center gap-3 border-t border-blue-100 pt-3">
          {isImage
            ? <img src={staged.previewUrl} alt="preview" className="w-16 h-16 object-cover rounded-lg border border-blue-200" />
            : <div className="w-16 h-16 bg-white rounded-lg border border-blue-200 flex flex-col items-center justify-center">
                <span className="text-2xl">📄</span><span className="text-xs text-slate-500 mt-1">PDF</span>
              </div>
          }
          <div className="text-sm text-blue-700">
            <p className="font-medium truncate max-w-xs">{staged.file.name}</p>
            <p className="text-xs text-blue-500">{(staged.file.size / 1024).toFixed(0)} KB</p>
            {staged.docNumber   && <p className="text-xs">Doc #: {staged.docNumber}</p>}
            {staged.uscisNumber && <p className="text-xs">USCIS #: {staged.uscisNumber}</p>}
            {staged.ssn4        && <p className="text-xs">SSN: ****{staged.ssn4}</p>}
            {staged.expiry      && !staged.naExpiry && <p className="text-xs">Expires: {new Date(staged.expiry).toLocaleDateString()}</p>}
            {staged.naExpiry    && <p className="text-xs">Expiry: N/A</p>}
          </div>
        </div>
      )}

      {reuploadReason && !isDone && (
        <div className="px-5 py-2.5 bg-orange-50 border-t border-orange-200 flex items-start gap-2">
          <span className="text-orange-500 text-sm shrink-0 mt-0.5">⚠</span>
          <p className="text-orange-700 text-xs"><strong>Reason for re-upload:</strong> {reuploadReason}</p>
        </div>
      )}

      {hasFailed && <div className="px-5 pb-3"><p className="text-red-600 text-xs">Upload failed. Will retry on submit.</p></div>}

      {/* File input — only shown when no file staged yet */}
      {!isStaged && (
        <div className="px-5 pb-0 pt-1 border-t border-slate-100">
          {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
          <div className="mb-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Select File {!isOptional && <span className="text-red-500">*</span>}
            </label>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile}
              className="block w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer" />
            <p className="text-xs text-slate-400 mt-1">PDF, JPG, PNG — max 20 MB</p>
          </div>
        </div>
      )}

      {/* Hidden replace input when staged */}
      {isStaged && <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} className="hidden" />}

      {/* Detail fields — always visible so user can fill/edit after selecting file */}
      {(meta.needsNumber || meta.needsUSCIS || meta.needsSSN4 || meta.needsExpiry) && (
        <div className={`px-5 pb-5 pt-3 space-y-3 ${isStaged ? 'border-t border-blue-100' : 'border-t border-slate-100'}`}>
          {error && isStaged && <p className="text-red-600 text-xs">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            {/* Document number */}
            {meta.needsNumber && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {docType === 'i20_certificate' ? 'SEVIS / Document Number' : 'Document Number'} <span className="text-red-500">*</span>
                </label>
                <input type="text" value={docNumber}
                  onChange={e => {
                    const v = e.target.value;
                    setDocNumber(v);
                    if (staged) onStage(docType, { ...staged, docNumber: v, uscisNumber, ssn4, expiry, naExpiry });
                  }}
                  placeholder={docType === 'passport' ? 'e.g. A12345678' : 'e.g. SER1234567'}
                  className={inputCls} />
              </div>
            )}

            {/* USCIS number (EAD only) */}
            {meta.needsUSCIS && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">USCIS Number <span className="text-red-500">*</span></label>
                <input type="text" value={uscisNumber}
                  onChange={e => {
                    const v = e.target.value;
                    setUscisNumber(v);
                    if (staged) onStage(docType, { ...staged, docNumber, uscisNumber: v, ssn4, expiry, naExpiry });
                  }}
                  placeholder="e.g. 000-123-456" className={inputCls} />
              </div>
            )}

            {/* SSN last 4 */}
            {meta.needsSSN4 && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Last 4 Digits of SSN <span className="text-red-500">*</span></label>
                <input type="text" maxLength={4} pattern="\d{4}" value={ssn4}
                  onChange={e => {
                    const v = e.target.value.replace(/\D/g, '');
                    setSsn4(v);
                    if (staged) onStage(docType, { ...staged, docNumber, uscisNumber, ssn4: v, expiry, naExpiry });
                  }}
                  placeholder="e.g. 1234" className={inputCls} />
              </div>
            )}

            {/* Expiry date */}
            {meta.needsExpiry && (
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Expiry Date {!naExpiry && <span className="text-red-500">*</span>}
                  {docType === 'ead_card' && (
                    <label className="ml-2 font-normal text-slate-500 cursor-pointer">
                      <input type="checkbox" checked={naExpiry}
                        onChange={e => { setNaExpiry(e.target.checked); syncDetails({ naExpiry: e.target.checked }); }}
                        className="mr-1 rounded" />
                      Not Applicable
                    </label>
                  )}
                </label>
                {!naExpiry
                  ? <input type="date" value={expiry}
                      onChange={e => {
                        const v = e.target.value;
                        setExpiry(v);
                        if (staged) onStage(docType, { ...staged, docNumber, uscisNumber, ssn4, expiry: v, naExpiry });
                      }}
                      className={inputCls} />
                  : <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">N/A selected</div>
                }
              </div>
            )}
          </div>
        </div>
      )}

      {preview && staged?.previewUrl && (
        <PreviewModal previewUrl={staged.previewUrl} mimeType={staged.file?.type} label={meta.label} onClose={() => setPreview(false)} />
      )}
    </div>
  );
}

// ── Main portal ────────────────────────────────────────────────────────────────
export default function PublicDocUpload() {
  const { token }  = useParams();
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [fullName,     setFullName]     = useState('');
  const [staged,       setStaged]       = useState({});
  const [submitting,   setSubmitting]   = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [allSubmitted, setAllSubmitted] = useState(false);
  const [submitError,  setSubmitError]  = useState('');

  useEffect(() => {
    pub.get('/candidates/upload_portal.php', { params: { token } })
      .then(res => {
        const d = res.data?.data || res.data;
        setData(d);
        const name = d?.candidate_name
          || `${d?.candidate?.first_name || ''} ${d?.candidate?.last_name || ''}`.trim();
        setFullName(name);
      })
      .catch(err => setError(err.response?.data?.message || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  function handleStage(docType, fileData) {
    setStaged(prev => ({ ...prev, [docType]: fileData }));
  }

  function handleRemove(docType) {
    setStaged(prev => {
      const next = { ...prev };
      if (next[docType]?.previewUrl) URL.revokeObjectURL(next[docType].previewUrl);
      delete next[docType];
      return next;
    });
  }

  async function handleSubmitAll() {
    setSubmitError('');

    if (!isSignedOffer && !fullName.trim()) { setSubmitError('Full name is required.'); return; }

    // ── Validate all mandatory fields inside each staged doc ─────────────────
    const fieldErrors = [];
    for (const docType of requiredDocs) {
      const s    = staged[docType];
      const meta = getMeta(docType);
      if (meta.isOptional) continue;
      if (!s) { fieldErrors.push(`${meta.label}: no file selected`); continue; }
      if (meta.isDegree) {
        if (!s.notApplicable && !s.file) fieldErrors.push(`${meta.label}: upload the file or mark as Not Completed`);
        continue;
      }
      if (!s.file) { fieldErrors.push(`${meta.label}: no file selected`); continue; }
      if (meta.needsNumber && !s.docNumber?.trim())
        fieldErrors.push(`${meta.label}: Document Number is required`);
      if (meta.needsUSCIS  && !s.uscisNumber?.trim())
        fieldErrors.push(`${meta.label}: USCIS Number is required`);
      if (meta.needsSSN4   && s.ssn4?.length !== 4)
        fieldErrors.push(`${meta.label}: Enter last 4 digits of SSN`);
      if (meta.needsExpiry && !s.naExpiry && !s.expiry)
        fieldErrors.push(`${meta.label}: Expiry Date is required`);
    }
    if (fieldErrors.length > 0) {
      setSubmitError(fieldErrors.join(' · '));
      return;
    }

    const docTypesToUpload = requiredDocs.filter(d => {
      const s = staged[d];
      if (!s) return false;
      const meta = getMeta(d);
      if (meta.isDegree && s.notApplicable) return true; // submit N/A marker for degree
      return !!s.file;
    });

    if (docTypesToUpload.length === 0) return;
    setSubmitting(true);

    const initial = {};
    docTypesToUpload.forEach(d => { initial[d] = 'uploading'; });
    setUploadStatus(initial);

    let allOk = true;
    for (const docType of docTypesToUpload) {
      const s = staged[docType];
      const fd = new FormData();
      fd.append('token',         token);
      fd.append('document_type', docType);
      fd.append('full_name',     fullName);

      if (s.notApplicable) {
        // N/A — send the flag only, no file
        fd.append('is_not_applicable', '1');
        fd.append('degree_type', 'not_completed');
      } else {
        fd.append('document_file', s.file);
        if (s.expiry     && !s.naExpiry) fd.append('expiry_date',    s.expiry);
        if (s.naExpiry)                  fd.append('expiry_na',      '1');
        if (s.docNumber)                 fd.append('document_number', s.docNumber);
        if (s.uscisNumber)               fd.append('uscis_number',   s.uscisNumber);
        if (s.ssn4)                      fd.append('ssn_last4',      s.ssn4);
        if (s.degreeType)                fd.append('degree_type',    s.degreeType);
      }

      try {
        await pub.post('/candidates/upload_document.php', fd);
        setUploadStatus(prev => ({ ...prev, [docType]: 'done' }));
      } catch {
        setUploadStatus(prev => ({ ...prev, [docType]: 'error' }));
        allOk = false;
      }
    }

    setSubmitting(false);
    if (allOk) setAllSubmitted(true);
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 text-sm">Loading your portal…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-2">Link Unavailable</h2>
        <p className="text-slate-500 text-sm">{error}</p>
        <p className="text-slate-400 text-xs mt-4">Contact your HR team for a new link.</p>
      </div>
    </div>
  );

  const candidate         = data?.candidate || {};
  const visaType          = data?.visa_type || candidate?.visa_type || '';
  const rawDocs           = data?.required_documents || [];
  const isReuploadSession = data?.is_reupload_session || false;
  const portalType        = data?.portal_type || 'documents';
  const isSignedOffer     = portalType === 'signed_offer';
  const reuploadReasons   = Object.fromEntries(
    rawDocs.filter(d => d.reupload_reason).map(d => [d.type, d.reupload_reason])
  );
  const candidateEmail = candidate.email || data?.candidate_email || '';

  // Use API list for signed offer portals and reupload sessions; VISA_DOCS for fresh doc portals
  const requiredDocs = (isReuploadSession || isSignedOffer)
    ? rawDocs.map(d => typeof d === 'string' ? d : d.type)
    : (VISA_DOCS[visaType] || VISA_DOCS.Other);

  // Count mandatory (non-optional) docs that still need to be staged
  const mandatoryDocs   = requiredDocs.filter(d => !getMeta(d).isOptional);
  const mandatoryStaged = mandatoryDocs.filter(d => {
    const s = staged[d];
    if (!s) return false;
    const meta = getMeta(d);
    if (meta.isDegree) return s.notApplicable || !!s.file; // degree: file OR marked not-completed
    return !!s.file;
  });
  // Counter shows mandatory docs only (excludes optional resume) so N/A selections count correctly
  const stagedCount       = mandatoryStaged.length;
  const total             = mandatoryDocs.length;
  const allStagedCount    = requiredDocs.filter(d => staged[d]?.file || staged[d]?.notApplicable).length;
  const allMandatoryReady = mandatoryStaged.length === mandatoryDocs.length;

  if (allSubmitted) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <svg className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Documents Submitted!</h2>
        <p className="text-slate-600 text-sm leading-relaxed">
          Thank you, <strong>{fullName}</strong>.{' '}
          {isReuploadSession
            ? 'Your re-uploaded document(s) have been submitted. HR will review them shortly.'
            : 'All documents have been submitted. Our HR team will review them and contact you shortly.'}
        </p>
        <div className="mt-6 bg-blue-50 rounded-xl px-4 py-3 text-blue-700 text-sm">You can safely close this window.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-blue-800 text-white sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">
              {isSignedOffer ? 'Signed Offer Letter Upload' : 'Document Upload Portal'}
            </h1>
            <p className="text-blue-200 text-sm">
              {candidateEmail && <span className="text-blue-300">{candidateEmail}</span>}
              {visaType && !isSignedOffer && <span className="ml-2 font-mono text-xs bg-white/20 px-1.5 py-0.5 rounded">{visaType}</span>}
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{stagedCount}<span className="text-blue-300 text-lg">/{total}</span></p>
            <p className="text-blue-300 text-xs">Ready to submit</p>
          </div>
        </div>
        <div className="h-1 bg-blue-900">
          <div className="h-1 bg-white transition-all duration-500" style={{ width: total > 0 ? `${(stagedCount / total) * 100}%` : '0%' }} />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-3">

        {/* Instructions */}
        {isSignedOffer ? (
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-800">
            <p className="font-semibold mb-1">📝 Upload Your Signed Offer Letter</p>
            <p>Please print the offer letter, sign it, and upload the signed copy below. Once submitted, HR will review and confirm your acceptance.</p>
          </div>
        ) : isReuploadSession ? (
          <div className="bg-orange-50 border border-orange-300 rounded-xl px-4 py-3 text-sm text-orange-800">
            <p className="font-semibold mb-1">⚠ Some documents need to be re-uploaded</p>
            <p>HR has reviewed your submission and flagged the document(s) below. Please re-upload only the requested documents.</p>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
            <strong>How it works:</strong> Fill in details for each document below and attach the file.
            Fields marked <span className="text-red-500 font-bold">*</span> are required. Click <strong>Submit All Documents</strong> when ready.
          </div>
        )}

        {/* Full name field — not needed on signed offer upload */}
        {!isSignedOffer && (
          <div className="bg-white rounded-xl border border-slate-200 px-5 py-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your full legal name"
            />
            <p className="text-xs text-slate-400 mt-1">Pre-filled from your record — edit if needed.</p>
          </div>
        )}

        {/* Document cards */}
        {requiredDocs.map(docType => {
          const meta = getMeta(docType);
          if (meta.isDegree) {
            return (
              <DegreeCard
                key={docType}
                docType={docType}
                staged={staged[docType] || null}
                onStage={handleStage}
                onRemove={handleRemove}
                uploadStatus={uploadStatus[docType] || null}
              />
            );
          }
          return (
            <DocCard
              key={docType}
              docType={docType}
              staged={staged[docType] || null}
              onStage={handleStage}
              onRemove={handleRemove}
              uploadStatus={uploadStatus[docType] || null}
              reuploadReason={reuploadReasons[docType] || null}
            />
          );
        })}

        {/* Submit button */}
        <div className="sticky bottom-0 bg-slate-50 pt-3 pb-6">
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700 mb-3 space-y-1">
              <p className="font-semibold mb-1">Please fix the following before submitting:</p>
              {submitError.split(' · ').map((e, i) => <p key={i}>• {e}</p>)}
            </div>
          )}
          {!allMandatoryReady && !submitError && (
            <p className="text-center text-slate-500 text-sm mb-3">
              {mandatoryDocs.length - mandatoryStaged.length} required document{mandatoryDocs.length - mandatoryStaged.length !== 1 ? 's' : ''} still needed
            </p>
          )}
          <button
            onClick={handleSubmitAll}
            disabled={submitting || !allMandatoryReady}
            className={`w-full py-4 rounded-xl text-base font-bold transition-all shadow-lg ${
              allMandatoryReady && !submitting
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
          >
            {submitting
              ? 'Uploading… please wait'
              : allMandatoryReady
              ? `✓ Submit All Documents`
              : 'Complete all required documents above'
            }
          </button>
        </div>

      </div>
    </div>
  );
}
